import { AdminPanelSettings, Assignment, Logout } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from '@mui/material';
import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, useNavigate } from 'react-router-dom';

import { useFeatureToggleReadyStatus } from '@cnx-saas/feature-toggle';

import { UserType } from '@cnx-saas/common';
import { useGetMe_Query } from '@cnx-saas/common/generated';
import { RequireAuth, useAuth } from '@cnx-saas/react-auth';

import { StyledApp, StyledContentContainer } from './app.style';
import { FallbackError } from './fallback-error';
import { Loading } from './loading';
import { Navigation } from './navigation';
import { useUserCentricsCMP, useViewport } from './navigation/sidebar/hooks';
import { Toolbar } from './navigation/toolbar';
import { Routes } from './routes';

import { PathEnum } from './navigation/sidebar/sidebar.type';
import { SidebarTranslationKeys } from './navigation/sidebar/translation-keys';

function AppWrapper(): JSX.Element {
  const areFeatureTogglesReady = useFeatureToggleReadyStatus();
  const { isSmallerScreen } = useViewport();

  if (!areFeatureTogglesReady) {
    return <Loading />;
  }

  return (
    <StyledApp>
      <BrowserRouter>
        <RequireAuth>
          <Navigation />
        </RequireAuth>
        <StyledContentContainer>
          {isSmallerScreen && (
            <RequireAuth>
              <Toolbar />
            </RequireAuth>
          )}

          <Box
            display={'flex'}
            flexDirection={'column'}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.light,
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <RequireAuth>
              <AvatarLogin />
            </RequireAuth>
            <Box
              sx={{
                flex: 1,
                marginLeft: 4,
                overflow: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '0.4em',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) => theme.palette.grey[400],
                  borderRadius: '4px',
                },
              }}
            >
              <ErrorBoundary FallbackComponent={FallbackError}>
                <Routes />
              </ErrorBoundary>
            </Box>
          </Box>
        </StyledContentContainer>
      </BrowserRouter>
    </StyledApp>
  );
}

export { AppWrapper };

function AvatarLogin() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { user } = useAuth(true);
  const { data: getMeData } = useGetMe_Query();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openConsentDialog } = useUserCentricsCMP();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'inherit',
        p: '4px',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <IconButton
        data-testid="avatar-button"
        onClick={handleClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar sx={{ width: 32, height: 32 }}></Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            width: 'max-content',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate(PathEnum.PATH_USER_SETTINGS)}>
          <Avatar />{' '}
          {`${getMeData?.getMe.firstName} ${getMeData?.getMe.lastName}`}
        </MenuItem>

        {user.userType === UserType.TEAM_ADMIN && (
          <MenuItem onClick={() => navigate(PathEnum.PATH_USER_MANAGEMENT)}>
            <ListItemIcon>
              <Assignment />
            </ListItemIcon>
            {t(SidebarTranslationKeys.USER_MANAGEMENT, {
              defaultValue: 'User Management',
            })}
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={() => openConsentDialog()}>
          <ListItemIcon>
            <AdminPanelSettings fontSize="small" />
          </ListItemIcon>
          {t(SidebarTranslationKeys.PRIVACY_SETTINGS, 'Privacy Settings') ??
            'Privacy Settings'}
        </MenuItem>
        <MenuItem onClick={() => navigate(PathEnum.PATH_LOGOUT)}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t(SidebarTranslationKeys.LOGOUT, 'Logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
}
